import {
  parseRtiDataThresholdInput,
  RTI_DATA_THRESHOLD_DEFAULT,
  type RtiMinimumDataThresholdModel,
} from 'utilities/rti-minimum-data-threshold';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

const INPUT_ID = 'rti-minimum-data-threshold-input';

export type RtiMinimumDataThresholdProps = {
  value: RtiMinimumDataThresholdModel;
  onChange: (next: RtiMinimumDataThresholdModel) => void;
  disabled?: boolean;
};

export function RtiMinimumDataThreshold({ value, onChange, disabled }: RtiMinimumDataThresholdProps) {
  const { t } = useTranslation();
  const errorId = useId();

  const [draft, setDraft] = useState(() => String(value.data_threshold));

  useEffect(() => {
    setDraft(String(value.data_threshold));
  }, [value.data_threshold]);

  const parsed = parseRtiDataThresholdInput(draft);
  const showNonNumeric = parsed.ok === false && parsed.error === 'nonNumeric';
  const showOutOfRange = parsed.ok === false && parsed.error === 'outOfRange';
  const hasInputError = showNonNumeric || showOutOfRange;

  const handleChange = (next: string) => {
    setDraft(next);
    const p = parseRtiDataThresholdInput(next);
    if (p.ok) {
      onChange({ data_threshold: p.value });
    }
  };

  const handleBlur = () => {
    const p = parseRtiDataThresholdInput(draft);
    if (p.ok) {
      setDraft(String(p.value));
      onChange({ data_threshold: p.value });
      return;
    }
    if (p.error === 'empty') {
      setDraft(String(RTI_DATA_THRESHOLD_DEFAULT));
      onChange({ data_threshold: RTI_DATA_THRESHOLD_DEFAULT });
      return;
    }
    setDraft(String(value.data_threshold));
  };

  return (
    <section className='flex flex-col gap-4' aria-labelledby='rti-minimum-data-threshold-title'>
      <div className='flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-10'>
        <div className='min-w-0 max-w-xl flex-1 lg:max-w-none'>
          <h3
            id='rti-minimum-data-threshold-title'
            className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-bold text-[#1D1D1D]'
          >
            {t('pe.settings.rti.minimumDataThreshold.title')}
          </h3>
          <p className='mt-2 font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#1D1D1D] lg:whitespace-nowrap'>
            {t('pe.settings.rti.minimumDataThreshold.helper')}
          </p>
        </div>

        <div
          className={`flex w-full shrink-0 flex-col gap-1 lg:w-[220px] lg:max-w-[220px] ${
            disabled ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          <label
            htmlFor={INPUT_ID}
            className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#1D1D1D]'
          >
            {t('pe.settings.rti.minimumDataThreshold.label')}
          </label>
          <input
            id={INPUT_ID}
            type='text'
            inputMode='numeric'
            autoComplete='off'
            aria-invalid={hasInputError}
            aria-describedby={hasInputError ? errorId : undefined}
            className='w-full rounded border border-[#C5C7CA] px-2 py-1.5 font-ods-font-family-secondary text-ods-font-styles-body-regular-small text-[#1D1D1D] placeholder:text-[#6C6F75] disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:opacity-60'
            value={draft}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            disabled={disabled}
          />
          <p className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#5D5C5C]'>
            {t('pe.settings.rti.minimumDataThreshold.rangeHint')}
          </p>
          {showNonNumeric ? (
            <p id={errorId} role='alert' className='text-ods-font-styles-body-regular-small text-red-600'>
              {t('pe.settings.rti.minimumDataThreshold.error.nonNumeric')}
            </p>
          ) : null}
          {showOutOfRange ? (
            <p id={errorId} role='alert' className='text-ods-font-styles-body-regular-small text-red-600'>
              {t('pe.settings.rti.minimumDataThreshold.error.outOfRange')}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
